function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { useCallback, useMemo } from 'react';
import { OnboardingWidgetState } from 'anchor-scout-common/types';
import { Button, Callout, FunctionalLoader, Page, Spinner, useOverlayContext } from 'seagull';
import RestfulWrapper from "../../components/RestfulWrapper/RestfulWrapper";
import { useIoContext, useStore, useStoreActions } from "../../contexts";
import { useFeatureFlags } from "../../hooks/useFeatureFlags";
import { useSetDocumentTitle } from "../../hooks/useSetDocumentTitle";
import { useStartKYC } from "../../hooks/useStartKYCPersona";
import InvoicesDigestWidget from "./InvoicesDigestWidget/InvoicesDigestWidget";
import { selectors } from "../../store/state-selectors";
import { CreateProposalButton } from "../../components/CreateProposalButton/CreateProposalButton";
import BusinessOnboardingWidget from "./BusinessOnboardingWidget/BusinessOnboardingWidget";
import BusinessSetupWidget from "./BusinessSetupWidget/BusinessSetupWidget";
import HistoricAgreementStatsWidget from "./HistoricAgreementStatsWidget/HistoricAgreementStatsWidget";
import { useGetInfo } from "./hooks/useGetInfo";
import { useUpdateOnboardingState } from "./hooks/useUpdateOnboardingState";
import PayoutsSummaryWidget from "./PayoutsSummaryWidget/PayoutsSummaryWidget";
import RevenueWidget from "./RevenueWidget/RevenueWidget";
import SalesWidget from "./SalesWidget/SalesWidget";
import styles from "./VendorDashboard.module.scss";
import { getKycWarning } from "./VendorDashboard.utils";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
export default function VendorDashboard() {
  const hasBusiness = useStore(selectors.user.hasBusiness);
  const userFirstName = useStore(selectors.user.firstName) || '';
  const isMemberUser = useStore(selectors.user.isMemberUser);
  const initialData = useStore(s => s.personaKYBStatus.data);
  const {
    fetchPersonaKYBStatus
  } = useStoreActions();
  const {
    NEW_VENDOR_ONBOARDING
  } = useFeatureFlags();
  const {
    startKYC
  } = useStartKYC();
  const {
    logBI
  } = useStoreActions();
  const {
    businessId,
    businessInfo,
    optimisticUpdate,
    isLoading
  } = useGetInfo();
  const {
    updateOnboardingState
  } = useUpdateOnboardingState();
  const {
    getNow
  } = useIoContext();
  const {
    getOpenedModals
  } = useOverlayContext();
  const coveredByModal = getOpenedModals().length > 0;
  const worksAt = useStore(selectors.user.business);
  const personaKYBStatus = useStore(s => s.personaKYBStatus.data);
  const kycWarning = getKycWarning(worksAt, personaKYBStatus != null && personaKYBStatus.timestamp > getNow().getTime() - 1000 * 60 * 5);
  useSetDocumentTitle('My Dashboard');
  const handleAnimationEnd = useCallback(expanded => {
    if (businessInfo) {
      const onboardingWidgetState = expanded ? OnboardingWidgetState.StaticOpened : OnboardingWidgetState.Hidden;
      updateOnboardingState({
        businessId: businessId,
        onboardingWidgetState
      });
      optimisticUpdate(old => _objectSpread(_objectSpread({}, old), {}, {
        onboardingWidgetState: onboardingWidgetState
      }));
    }
  }, [businessId, businessInfo, updateOnboardingState, optimisticUpdate]);
  const setupWidgetViewingState = useMemo(() => {
    if (coveredByModal && (businessInfo === null || businessInfo === void 0 ? void 0 : businessInfo.onboardingWidgetState) === OnboardingWidgetState.ShouldReveal) {
      return OnboardingWidgetState.Hidden;
    }
    return businessInfo === null || businessInfo === void 0 ? void 0 : businessInfo.onboardingWidgetState;
  }, [businessInfo === null || businessInfo === void 0 ? void 0 : businessInfo.onboardingWidgetState, coveredByModal]);
  if (hasBusiness && isLoading) return /*#__PURE__*/_jsx(FunctionalLoader, {
    show: true
  });
  const renderOnboardingWidget = () => NEW_VENDOR_ONBOARDING ? setupWidgetViewingState ? /*#__PURE__*/_jsx(BusinessSetupWidget, {
    viewingState: setupWidgetViewingState,
    onAnimationEnd: handleAnimationEnd
  }) : null : /*#__PURE__*/_jsx("div", {
    className: styles.onboardingWidgetContainer,
    children: /*#__PURE__*/_jsx(BusinessOnboardingWidget, {
      className: styles.fullWidth
    })
  });
  const renderDashboard = () => /*#__PURE__*/_jsxs(_Fragment, {
    children: [kycWarning && /*#__PURE__*/_jsx(Callout, {
      intent: "warning",
      hideIcon: true,
      className: styles.kycIncompleteBanner,
      "data-testid": kycWarning.testid,
      withAction: /*#__PURE__*/_jsx(Button, {
        onClick: () => {
          logBI(11203);
          startKYC();
        },
        skin: "darkSecondary",
        children: "Complete verification"
      }),
      children: kycWarning.text
    }), !isMemberUser ? renderOnboardingWidget() : null, /*#__PURE__*/_jsxs("div", {
      className: styles.widgets,
      "data-testid": "vendor-dashboard-widgets",
      children: [/*#__PURE__*/_jsx(InvoicesDigestWidget, {}), /*#__PURE__*/_jsx(HistoricAgreementStatsWidget, {}), /*#__PURE__*/_jsx(PayoutsSummaryWidget, {}), /*#__PURE__*/_jsx(RevenueWidget, {}), /*#__PURE__*/_jsx(SalesWidget, {})]
    })]
  });
  return /*#__PURE__*/_jsx(Page, {
    "data-testid": "vendor-dashboard",
    header: "Hi ".concat(userFirstName, "!"),
    actions: /*#__PURE__*/_jsx(CreateProposalButton, {}),
    minWidth: "1090px",
    children: hasBusiness ? /*#__PURE__*/_jsx(RestfulWrapper, {
      initialData: initialData,
      fetch: fetchPersonaKYBStatus,
      renderChild: renderDashboard,
      renderLoader: () => /*#__PURE__*/_jsx(Spinner, {
        "data-testid": "vendor-dashboard-loading",
        centeredInBox: true
      })
    }) : /*#__PURE__*/_jsx("div", {
      "data-testid": "vendor-dashboard-no-business"
    })
  });
}